import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OTPInput from '../components/auth/OTPInput';
import api from '../services/api';

/**
 * Page de connexion par OTP (One-Time Password)
 *
 * Utilisé pour l'authentification CLIENT sans mot de passe
 * - Saisie de l'email
 * - Envoi du code OTP par email
 * - Vérification du code
 * - Création automatique du compte si n'existe pas
 */
const LoginOTP = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login: authLogin, user } = useAuth();

  // États du formulaire
  const [step, setStep] = useState('email'); // 'email' ou 'otp'
  const [email, setEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Ref pour éviter la double exécution en mode Strict
  const otpRequestedRef = useRef(false);

  // Rediriger si déjà connecté
  useEffect(() => {
    if (user) {
      navigate('/client-dashboard');
    }
  }, [user, navigate]);

  // Vérifier le paramètre user_email dans l'URL
  useEffect(() => {
    const userEmailParam = searchParams.get('user_email');

    // Ne s'exécute qu'une seule fois grâce au ref + vérification sessionStorage
    // sessionStorage empêche le renvoi lors des rechargements HMR (dev mode)
    const sessionKey = `otp_sent_${userEmailParam}`;
    const alreadySent = sessionStorage.getItem(sessionKey);

    if (userEmailParam && !otpRequestedRef.current && !alreadySent) {
      otpRequestedRef.current = true; // Marquer comme exécuté dans le composant
      sessionStorage.setItem(sessionKey, 'true'); // Marquer en session pour HMR

      // Fermer toute session active
      localStorage.removeItem('boaz_user');

      // Pré-remplir l'email et envoyer l'OTP automatiquement
      setEmail(userEmailParam.toLowerCase());
      handleRequestOTP(userEmailParam.toLowerCase(), true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /**
   * Demander un code OTP
   */
  const handleRequestOTP = async (emailToUse = null, isAutomatic = false) => {
    const emailValue = emailToUse || email;

    if (!emailValue || !emailValue.includes('@')) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/auth/request-otp', {
        email: emailValue.toLowerCase()
      });

      if (response.data.success) {
        setMaskedEmail(response.data.email);
        setStep('otp');
        setSuccess(isAutomatic
          ? `Code envoyé à ${response.data.email}. Consultez votre boîte mail.`
          : `Code envoyé ! Consultez votre boîte mail ${response.data.email}`
        );
      } else {
        setError(response.data.message || 'Erreur lors de l\'envoi du code');
      }
    } catch (err) {
      console.error('Erreur requête OTP:', err);

      if (err.response?.status === 429) {
        setError('Trop de tentatives. Veuillez réessayer dans 15 minutes.');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Erreur lors de l\'envoi du code. Veuillez réessayer.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Vérifier le code OTP et authentifier
   */
  const handleVerifyOTP = async (code) => {
    if (!code || code.length !== 6) {
      setError('Veuillez entrer un code à 6 chiffres');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/verify-otp', {
        email: email.toLowerCase(),
        code: code
      });

      if (response.data.success) {
        const userData = response.data.user;

        // Stocker l'utilisateur en localStorage
        localStorage.setItem('boaz_user', JSON.stringify(userData));

        // Message de bienvenue
        const welcomeMsg = response.data.user_created
          ? 'Compte créé avec succès ! Bienvenue 👋'
          : 'Connexion réussie ! Bienvenue 👋';

        setSuccess(welcomeMsg);

        // Redirection après 1 seconde
        setTimeout(() => {
          navigate('/client-dashboard');
          window.location.reload();
        }, 1000);
      } else {
        setError(response.data.message || 'Code incorrect');
      }
    } catch (err) {
      console.error('Erreur vérification OTP:', err);

      if (err.response?.status === 401) {
        setError(err.response.data?.detail || 'Code incorrect ou expiré');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Erreur lors de la vérification. Veuillez réessayer.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Renvoyer un nouveau code OTP
   */
  const handleResendOTP = () => {
    handleRequestOTP();
  };

  /**
   * Retour à l'étape email
   */
  const handleBackToEmail = () => {
    setStep('email');
    setError('');
    setSuccess('');

    // Nettoyer le sessionStorage pour permettre un nouveau envoi si besoin
    const userEmailParam = searchParams.get('user_email');
    if (userEmailParam) {
      sessionStorage.removeItem(`otp_sent_${userEmailParam}`);
      otpRequestedRef.current = false;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl border border-gray-100">

        {/* Logo et titre */}
        <div className="text-center">
          <div className="mx-auto w-32 h-32 mb-6">
            <img
              src="/logo-boaz-housing.png"
              alt="Boaz Housing"
              className="w-full h-full object-contain"
            />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {step === 'email' ? 'Connexion Client' : 'Vérification'}
          </h2>
          <p className="text-gray-600 text-sm">
            {step === 'email'
              ? 'Connectez-vous avec votre email'
              : `Code envoyé à ${maskedEmail}`
            }
          </p>
        </div>

        {/* Messages d'erreur et succès */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        )}

        {/* Formulaire - Étape Email */}
        {step === 'email' && (
          <form onSubmit={(e) => { e.preventDefault(); handleRequestOTP(); }} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Adresse email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="client@example.com"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-500 to-orange-500 hover:from-blue-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition duration-150 ease-in-out transform hover:scale-[1.02]"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Envoi en cours...
                </div>
              ) : (
                <>
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Recevoir le code
                </>
              )}
            </button>

            {/* Séparateur OU */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">OU</span>
              </div>
            </div>

            {/* Bouton connexion avec mot de passe */}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full flex justify-center items-center py-3 px-4 border-2 border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition duration-150 ease-in-out"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              Me connecter avec mon mot de passe
            </button>
          </form>
        )}

        {/* Formulaire - Étape OTP */}
        {step === 'otp' && (
          <div className="space-y-6">
            {/* Composant OTP Input */}
            <OTPInput
              length={6}
              onComplete={handleVerifyOTP}
              disabled={isLoading}
            />

            {/* Informations de sécurité */}
            <div className="bg-gradient-to-r from-blue-50 to-orange-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-gray-700">
                  <p className="font-medium mb-1">⏰ Informations importantes</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Code valide pendant <strong>1 heure</strong></li>
                    <li>Maximum <strong>3 tentatives</strong> par code</li>
                    <li>Ne partagez jamais ce code</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isLoading}
                className="w-full flex justify-center items-center py-2.5 px-4 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Renvoyer le code
              </button>

              <button
                type="button"
                onClick={handleBackToEmail}
                disabled={isLoading}
                className="w-full flex justify-center items-center py-2.5 px-4 text-sm font-medium rounded-xl text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition duration-150"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Modifier l'email
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginOTP;
